import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar as CalendarComp } from "@/components/ui/calendar";
import { CalendarDays, Users, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const MODE_COLOR = {
  "In-person": "bg-emerald-50 text-emerald-800",
  Phone: "bg-blue-50 text-blue-800",
  Video: "bg-violet-50 text-violet-800",
  WhatsApp: "bg-lime-50 text-lime-800",
};
const STATUS_COLOR = {
  scheduled: "bg-brand-50 text-brand-800",
  done: "bg-emerald-100 text-emerald-800",
  no_show: "bg-rose-50 text-rose-700",
  rescheduled: "bg-amber-50 text-amber-800",
  cancelled: "bg-slate-100 text-slate-700",
};

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59);
}
function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function CalendarPage() {
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [selected, setSelected] = useState(new Date());
  const [items, setItems] = useState([]);
  const [mineOnly, setMineOnly] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/interviews", {
        params: {
          start: startOfMonth(month).toISOString(),
          end: endOfMonth(month).toISOString(),
          mine: mineOnly || undefined,
        },
      });
      setItems(data.items);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, mineOnly]);

  const byDay = useMemo(() => {
    const m = new Map();
    items.forEach((i) => {
      const k = ymd(new Date(i.scheduled_at));
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(i);
    });
    return m;
  }, [items]);

  const selectedDayItems = byDay.get(ymd(selected)) || [];

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/interviews/${id}`, { status });
      toast.success("Updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeInterview = async (id) => {
    if (!window.confirm("Delete this interview?")) return;
    try {
      await api.delete(`/interviews/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <AppShell
      title="Interview Calendar"
      subtitle={`${items.length} interview${items.length !== 1 ? "s" : ""} in ${month.toLocaleString("default", { month: "long", year: "numeric" })}`}
      actions={
        <Button
          data-testid="calendar-mine-toggle"
          onClick={() => setMineOnly((v) => !v)}
          variant={mineOnly ? "default" : "outline"}
          className={
            mineOnly
              ? "bg-brand-700 hover:bg-brand-800 text-white"
              : "border-brand-200 text-brand-700 hover:bg-brand-50"
          }
        >
          {mineOnly ? "Showing: Mine" : "Show only mine"}
        </Button>
      }
    >
      <div className="grid lg:grid-cols-[420px_1fr] gap-6">
        <Card className="tj-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <button
                data-testid="prev-month"
                onClick={() =>
                  setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))
                }
                className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-100"
              >
                <ChevronLeft size={16} />
              </button>
              <h3 className="font-heading text-base font-semibold text-ink-900">
                {month.toLocaleString("default", { month: "long", year: "numeric" })}
              </h3>
              <button
                data-testid="next-month"
                onClick={() =>
                  setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))
                }
                className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-100"
              >
                <ChevronRight size={16} />
              </button>
            </div>
            <CalendarComp
              mode="single"
              month={month}
              onMonthChange={setMonth}
              selected={selected}
              onSelect={(d) => d && setSelected(d)}
              modifiers={{
                hasInterview: (day) => byDay.has(ymd(day)),
              }}
              modifiersClassNames={{
                hasInterview:
                  "relative font-semibold !text-brand-800 after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:bg-brand-600 after:rounded-full",
              }}
              className="rounded-lg border"
              data-testid="calendar-grid"
            />
            <div className="mt-3 text-xs text-ink-600 flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-600" />
              <span>= day with scheduled interviews</span>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-3" data-testid="day-interviews">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-lg font-semibold text-ink-900 flex items-center gap-2">
              <CalendarDays size={18} className="text-brand-700" />
              {selected.toLocaleDateString(undefined, {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </h3>
            <Badge className="bg-slate-100 text-ink-700 border-0">
              {selectedDayItems.length} interview
              {selectedDayItems.length !== 1 ? "s" : ""}
            </Badge>
          </div>

          {selectedDayItems.length === 0 ? (
            <Card className="tj-card">
              <CardContent className="p-8 text-center text-ink-600">
                <p className="text-sm">No interviews scheduled on this day.</p>
                <p className="text-xs mt-1">
                  Open a candidate&apos;s profile and click <strong>Schedule
                  Interview</strong> to add one.
                </p>
              </CardContent>
            </Card>
          ) : (
            selectedDayItems
              .sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at))
              .map((iv) => (
                <Card key={iv.id} className="tj-card">
                  <CardContent className="p-4 md:p-5">
                    <div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-5">
                      <div className="w-20 shrink-0 text-center">
                        <div className="font-heading font-semibold text-lg text-brand-800">
                          {new Date(iv.scheduled_at).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-ink-600">
                          {iv.mode || "In-person"}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <Link
                            to={`/candidates/${iv.candidate_id}`}
                            data-testid={`iv-cand-${iv.id}`}
                            className="font-heading font-semibold text-ink-900 hover:text-brand-700"
                          >
                            {iv.candidate_name}
                          </Link>
                          <Badge
                            className={`${
                              STATUS_COLOR[iv.status] || "bg-slate-100"
                            } border-0`}
                          >
                            {iv.status.replace("_", " ")}
                          </Badge>
                          <Badge
                            className={`${
                              MODE_COLOR[iv.mode] || "bg-slate-100"
                            } border-0`}
                          >
                            {iv.mode || "In-person"}
                          </Badge>
                        </div>
                        <div className="mt-1 text-sm text-ink-700">
                          {iv.role && <strong>{iv.role}</strong>}{" "}
                          {iv.role && "at "} {iv.company_name}
                        </div>
                        {iv.location && (
                          <div className="mt-1 text-xs text-ink-600">
                            📍 {iv.location}
                          </div>
                        )}
                        {iv.notes && (
                          <p className="mt-2 text-xs text-ink-600 italic">
                            “{iv.notes}”
                          </p>
                        )}
                        <div className="text-[11px] text-ink-600 mt-2">
                          Added by {iv.created_by_name}
                          {iv.candidate_phone && ` · ${iv.candidate_phone}`}
                        </div>
                      </div>
                      <div className="flex md:flex-col gap-2">
                        <Select
                          value={iv.status}
                          onValueChange={(v) => updateStatus(iv.id, v)}
                        >
                          <SelectTrigger
                            data-testid={`iv-status-${iv.id}`}
                            className="h-8 text-xs w-[140px]"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[
                              "scheduled",
                              "done",
                              "no_show",
                              "rescheduled",
                              "cancelled",
                            ].map((s) => (
                              <SelectItem key={s} value={s}>
                                {s.replace("_", " ")}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeInterview(iv.id)}
                          data-testid={`iv-del-${iv.id}`}
                          className="border-rose-200 text-rose-700 hover:bg-rose-50"
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
          )}
        </div>
      </div>
    </AppShell>
  );
}
